import { moduleOrigins, type ModuleOrigins } from '../modules';
import { PartnersBand, type PartnerLogo } from './PartnersBand';
import { SiteFooter, type SiteFooterProps } from './SiteChrome';
import { siteNav, type SiteNavOptions } from '../siteNav';

/** Redes sociais da Grow uP (as mesmas do site antigo). */
export const SOCIAL_LINKS = [
  { label: 'Instagram', href: 'https://instagram.com/growupesports' },
  { label: 'Facebook', href: 'https://facebook.com/growupesports' },
  { label: 'X', href: 'https://x.com/growupesports' },
  { label: 'YouTube', href: 'https://youtube.com/growupesports' },
  { label: 'Twitch', href: 'https://twitch.tv/team/growupesports' },
  { label: 'LinkedIn', href: 'https://linkedin.com/company/grow-up-esports' },
  { label: 'Discord', href: 'https://discord.gg/J36KkXj' },
];

type FooterColumns = NonNullable<SiteFooterProps['columns']>;
type FooterLinks = NonNullable<SiteFooterProps['legalLinks']>;

export type PublicFooterOptions = SiteNavOptions;

/**
 * O mapa do site no rodape (requisitos-novos/mapa-do-site.md): os mesmos
 * grupos do cabecalho (`siteNav`), com Notícias em «Grow uP» e uma coluna
 * «Conta». Igual em todos os modulos: no site as paginas ficam relativas, nos
 * outros levam o endereco do site, deduzido do dominio como o seletor.
 */
export function publicFooterColumns(
  current: 'site' | 'streaming',
  origins: ModuleOrigins = moduleOrigins(),
  options: PublicFooterOptions = {},
): FooterColumns {
  const site = (path: string) => (current === 'site' ? path : `${origins.site}${path}`);
  const columns: FooterColumns = [];
  let news: { label: string; href: string } | null = null;
  for (const entry of siteNav(current, origins, options)) {
    if ('items' in entry) {
      columns.push({
        title: entry.label,
        links: entry.items.map(({ label, href }) => ({ label, href })),
      });
    } else if (entry.label === 'Notícias') {
      news = { label: entry.label, href: entry.href };
    }
  }
  for (const column of columns) {
    // Candidatura a streamer: no Stream Portal, logo a seguir aos Streamers.
    if (column.title === 'Comunidade') {
      const at = column.links.findIndex((l) => l.label === 'Streamers') + 1;
      column.links.splice(at, 0, {
        label: 'Ser streamer',
        href: `${current === 'streaming' ? '' : origins.streaming}/candidatura`,
      });
    }
    if (column.title === 'Grow uP' && news) column.links.unshift(news);
  }
  columns.push({
    title: 'Conta',
    links: [
      { label: 'A minha conta', href: site('/conta') },
      { label: 'Criar conta', href: site('/conta/registar') },
      { label: 'Área dos pais', href: site('/conta/encarregado') },
      { label: 'Loja', href: site('/loja') },
    ],
  });
  return columns;
}

/** A linha final: privacidade, termos e o Livro de Reclamações. */
export function publicFooterLegalLinks(
  current: 'site' | 'streaming',
  origins: ModuleOrigins = moduleOrigins(),
): FooterLinks {
  const site = (path: string) => (current === 'site' ? path : `${origins.site}${path}`);
  return [
    { label: 'Privacidade', href: site('/privacidade') },
    { label: 'Termos', href: site('/termos') },
    { label: 'Livro de Reclamações', href: 'https://www.livroreclamacoes.pt' },
  ];
}

export type PublicFooterProps = {
  /** O modulo onde a pagina esta aberta. */
  current: 'site' | 'streaming';
  /** Ha homenagens no memorial (a ligacao so aparece entao). */
  memorial?: boolean;
  /** Ha premios recebidos publicados (a ligacao so aparece entao). */
  awards?: boolean;
  /** Ha parceiros publicados (a ligacao so aparece entao). */
  partners?: boolean;
  /** Os logotipos do carrossel por cima do rodape; vazio = sem faixa. */
  partnerLogos?: PartnerLogo[];
};

/** O rodape de todas as apps publicas (site, conta, Stream Portal). */
export function PublicFooter({
  current,
  memorial = false,
  awards = false,
  partners = false,
  partnerLogos = [],
}: PublicFooterProps) {
  const origins = moduleOrigins();
  const site = current === 'site' ? '' : origins.site;
  return (
    <>
      <PartnersBand partners={partnerLogos} allHref={`${site}/parceiros`} />
      <SiteFooter
        columns={publicFooterColumns(current, origins, { memorial, awards, partners })}
        social={SOCIAL_LINKS}
        legalLinks={publicFooterLegalLinks(current, origins)}
        legal={<span>© 2002–{new Date().getFullYear()} Grow uP Esports</span>}
      />
    </>
  );
}
